import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTenant } from '../config/tenantConfig';
import { useDarkMode } from '../DarkModeContext';
import { useAuth } from '../context/AuthContext';
import GlassTopBar from '../components/GlassTopBar';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import SupportChatAgent from '../components/SupportChatAgent';
import LandingGuideAgent from '../components/LandingGuideAgent';
import Swal from 'sweetalert2';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  FileText,
  Gift,
  Settings
} from 'lucide-react';

const REFERRAL_LOGO = `${process.env.PUBLIC_URL || ''}/assets/halitrade-logo-red.svg`;

/**
 * Layout pour l'application Referral
 * Interface pour les partenaires référents
 * Uses GlassTopBar + CollapsibleSidebar for consistent glass-morphism design
 */
const ReferralLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantName, theme } = useTenant();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { user: authUser, isAuthenticated, logout } = useAuth();
  const currentUser = useSelector(state => state.userManagement.currentUser);
  const referralStats = useSelector(state => state.referral?.stats);
  const displayUser = currentUser || authUser || null;
  const displayName = displayUser
    ? `${displayUser.firstName || ''} ${displayUser.lastName || ''}`.trim() || displayUser.email
    : '';
  const displayRole = displayUser?.role || displayUser?.category || 'Referral Partner';
  const userEmail = displayUser?.email || '';
  const userCategory = displayUser?.category || 'referral';

  // On mobile, sidebar overlays — no margin shift needed
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  const sidebarWidth = isMobile ? 0 : (isSidebarOpen ? (sidebarCollapsed ? 80 : 256) : 0);

  // Icon color map for sidebar items
  const iconColorMap = {
    'Dashboard': '#60a5fa',
    'My Opportunities': '#a855f7',
    'Submit New': '#34d399',
    'My Wallet': '#14b8a6',
    'My Companies': '#22d3ee',
    'Settings': '#f97316'
  };

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/referral/dashboard',
      color: iconColorMap['Dashboard']
    },
    {
      icon: Users,
      label: 'My Opportunities',
      path: '/referral/opportunities',
      badge: referralStats?.active || 0,
      color: iconColorMap['My Opportunities']
    },
    {
      icon: Gift,
      label: 'Submit New',
      path: '/referral/refer',
      color: iconColorMap['Submit New']
    },
    {
      icon: DollarSign,
      label: 'My Wallet',
      path: '/referral/wallet',
      color: iconColorMap['My Wallet']
    },
    {
      icon: FileText,
      label: 'My Companies',
      path: '/referral/company',
      color: iconColorMap['My Companies']
    },
    {
      icon: Settings,
      label: 'Settings',
      path: '/referral/settings',
      color: iconColorMap['Settings']
    }
  ];

  const handleLogout = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Confirm exit',
      text: 'Are you sure you want to leave the referral portal?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, exit',
      cancelButtonText: 'Stay',
      reverseButtons: true,
      background: darkMode ? '#1F2937' : '#fff',
      color: darkMode ? '#F9FAFB' : '#111827',
      confirmButtonColor: theme.primary
    });

    if (isConfirmed) {
      logout();
      navigate('/haliflow-landing');
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarNavigate = (path) => {
    navigate(path);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleBrandClick = () => {
    navigate('/referral/dashboard');
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header — GlassTopBar (consistent with all other modules) */}
      <GlassTopBar
        logoSrc={REFERRAL_LOGO}
        brandName="HaliTrade"
        brandSubtitle="Referral Partner"
        displayRole={displayRole}
        displayName={displayName}
        userEmail={userEmail}
        userCategory={userCategory}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        isSidebarCollapsed={!isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onBrandClick={handleBrandClick}
        notifications={referralStats?.pending || 0}
        onLogout={handleLogout}
        primaryColor={theme.primary}
      />

      <div className="flex">
        {/* Sidebar — CollapsibleSidebar with glass morphism */}
        <CollapsibleSidebar
          className={!isSidebarOpen ? 'sidebar-hidden' : ''}
          style={{ top: '96px' }}
          logoSrc={REFERRAL_LOGO}
          brandName="HaliTrade"
          platformName="HaliTrade"
          subtitle="Powered by HaliSoft"
          viewBadge="Referral View"
          menuItems={menuItems}
          activePath={location.pathname}
          onNavigate={handleSidebarNavigate}
          onLogoClick={() => navigate('/referral/dashboard')}
          onToggle={(collapsed) => setSidebarCollapsed(collapsed)}
          primaryColor={theme.primary}
          darkMode={darkMode}
          onLogout={handleLogout}
        />

        {/* Main Content — glass panel wrapper */}
        <main
          className="flex-1 overflow-auto transition-all duration-300 ease-in-out"
          style={{ marginLeft: sidebarWidth }}
        >
          <div className="p-6 lg:p-8">
            <div className="glass-surface glass-panel rounded-[28px] p-6 lg:p-8 shadow-[0_35px_120px_rgba(15,23,42,0.15)]">
              {children || <Outlet />}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Support Chat */}
      <SupportChatAgent />

      <LandingGuideAgent darkMode={darkMode} theme="haliagent" position="left" />
    </div>
  );
};

export default ReferralLayout;
